import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { getAffiliatePageStyles } from '../affiliates/affiliateStyles';
import '../affiliates/affiliates.css';
import './deals.css';

function formatRs(n) {
  return `Rs ${Number(n || 0).toLocaleString('en-PK')}`;
}

export default function DealsList() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, dealId: null, dealName: '' });
  const [deleting, setDeleting] = useState(false);

  const styles = getAffiliatePageStyles(isMobile, isSmallMobile);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/deals/admin/all`);
      const result = await parseJsonResponse(response);
      setDeals(result.data || []);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to load deals.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const openDeleteModal = (id, name) => {
    setConfirmDelete({ open: true, dealId: id, dealName: name });
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setConfirmDelete({ open: false, dealId: null, dealName: '' });
  };

  const confirmDeletion = async () => {
    const { dealId } = confirmDelete;
    if (!dealId) return;

    setDeleting(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/deals/${dealId}`, { method: 'DELETE' });
      await parseJsonResponse(response);
      toast.success('Deal deleted');
      setConfirmDelete({ open: false, dealId: null, dealName: '' });
      fetchDeals();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to delete deal.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={styles.mainWrapper}>
      <Toaster position="top-center" />
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div>
            <h1 style={styles.headerTitle}>Deals</h1>
            <p style={styles.breadcrumbs}>Home • Deal List</p>
          </div>
          <button
            type="button"
            style={styles.primaryButton}
            onClick={() => navigate('/deals/add')}
          >
            <FaPlus style={{ marginRight: 6 }} />
            Create Deal
          </button>
        </div>

        <div style={styles.contentContainer}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Image', 'Deal Name', 'Products', 'Deal Price', 'Savings', 'Status', 'Actions'].map(
                    (h) => (
                      <th key={h} style={styles.th}>
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={styles.emptyCell}>
                      Loading deals...
                    </td>
                  </tr>
                ) : deals.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="deal-empty-state">
                        <p>No deals yet. Create your first bundle deal.</p>
                        <button
                          type="button"
                          style={styles.primaryButton}
                          onClick={() => navigate('/deals/add')}
                        >
                          <FaPlus style={{ marginRight: 6 }} />
                          Create Deal
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  deals.map((deal) => {
                    const savings = Math.max(0, (deal.originalPrice || 0) - (deal.dealPrice || 0));
                    const isActive = deal.status === 'active';
                    const coverImage = (Array.isArray(deal.images) && deal.images[0]) || deal.image;

                    return (
                      <tr key={deal._id}>
                        <td style={styles.td}>
                          {coverImage ? (
                            <img src={coverImage} alt={deal.name} className="deal-thumb" />
                          ) : (
                            <div className="deal-thumb deal-thumb--empty">No image</div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span className="deal-name-cell">{deal.name}</span>
                        </td>
                        <td style={styles.td}>{deal.items?.length || 0}</td>
                        <td style={styles.td}>
                          <span className="deal-price-sale">{formatRs(deal.dealPrice)}</span>
                          {deal.originalPrice > deal.dealPrice && (
                            <span className="deal-price-was">{formatRs(deal.originalPrice)}</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          {savings > 0 ? (
                            <span className="deal-save-text">{formatRs(savings)}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td style={styles.td}>
                          <span className={isActive ? 'deal-status-active' : 'deal-status-inactive'}>
                            {deal.status}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionGroup}>
                            <button
                              type="button"
                              style={styles.secondaryButton}
                              onClick={() => navigate(`/deals/edit/${deal._id}`)}
                            >
                              <FaEdit style={{ marginRight: 4 }} />
                              Edit
                            </button>
                            <button
                              type="button"
                              style={styles.dangerButton}
                              onClick={() => openDeleteModal(deal._id, deal.name)}
                            >
                              <FaTrash style={{ marginRight: 4 }} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {confirmDelete.open && (
        <ConfirmDeleteModal
          open
          message={
            <>
              Are you sure you want to delete{' '}
              <strong>&quot;{confirmDelete.dealName}&quot;</strong>? This action cannot be undone.
            </>
          }
          loading={deleting}
          onConfirm={confirmDeletion}
          onCancel={closeDeleteModal}
        />
      )}
    </div>
  );
}
